/**
 * An order, its payments, and what is still owed.
 *
 * The kiosk builds one of these from a cart and posts it once. The server
 * re-prices it from its own catalogue and refuses a mismatch — which is the
 * whole reason this arithmetic is a package both of them import rather than a
 * function each of them has.
 */
import type { OrderType, Sku } from './catalogue'
import type { PricedLine } from './line'
import type { Money } from './money'
import { ZERO, add, compare, isNegative, max, min, splitVatInclusive, sub, sum, times, toTugrik } from './money'

export type PaymentMethod = 'cash' | 'card' | 'qpay'

export type PaymentState =
  /** Created but not yet confirmed — a QPay invoice showing on screen. */
  | 'pending'
  /** The money is taken. A terminal approval or a QPay callback. */
  | 'captured'
  | 'failed'
  | 'cancelled'

export interface Payment {
  readonly id: string
  readonly method: PaymentMethod
  readonly amount: Money
  readonly state: PaymentState
  /** Terminal approval code, QPay invoice id. What proves the money moved. */
  readonly reference?: string
  /** Masked PAN, terminal id, QPay short url. Display and support. */
  readonly detail?: Record<string, string>
  readonly at: string
}

/** VAT registration details, when the customer is buying as a company. */
export interface CompanyReceipt {
  readonly registrationNumber: string
  readonly name: string
  readonly tin?: string
}

export interface OrderDraft {
  readonly id: string
  readonly branchId: string
  readonly deviceId: string
  readonly orderType: OrderType
  readonly lines: readonly PricedLine[]
  readonly payments: readonly Payment[]
  readonly company: CompanyReceipt | null
  readonly createdAt: string
}

export interface OrderTotals {
  readonly gross: Money
  readonly net: Money
  readonly vat: Money
  readonly savings: Money
  readonly paid: Money
  readonly due: Money
  readonly change: Money
}

/** The VAT rate, in whole percent, applied inclusively. */
export const VAT_PERCENT = 10

/**
 * Everything a payment screen needs, from the lines and the payments.
 *
 * `paid` counts only `captured`. A pending QPay invoice is not money, and a
 * screen that treats it as money is a screen that lets a customer walk out with
 * a coffee they have not paid for.
 *
 * VAT is computed on the **order** rather than summed per line, so the receipt's
 * tax figure is the tax on the amount actually charged. Summing per-line VAT
 * lands within a unit of it and disagrees with the total the customer paid,
 * which is exactly the reconciliation nobody can explain a month later.
 */
export function totalsOf(order: Pick<OrderDraft, 'lines' | 'payments'>): OrderTotals {
  const gross = order.lines.length === 0 ? ZERO : sum(order.lines.map((l) => l.total))
  const savings = order.lines.length === 0 ? ZERO : sum(order.lines.map((l) => l.savings))
  const { net, vat } = splitVatInclusive(gross, VAT_PERCENT)

  const captured = order.payments.filter((p) => p.state === 'captured')
  const paid = captured.length === 0 ? ZERO : sum(captured.map((p) => p.amount))

  const balance = sub(gross, paid)
  // Change is handed back in cash and nothing else: a card or QPay charge is
  // the total rounded up to a whole tugrik, and that surplus is not change.
  const cash = sum(captured.filter((p) => p.method === 'cash').map((p) => p.amount))
  return {
    gross,
    net,
    vat,
    savings,
    paid,
    due: max(balance, ZERO),
    change: isNegative(balance) ? min(sub(paid, gross), cash) : ZERO
  }
}

export const isSettled = (order: Pick<OrderDraft, 'lines' | 'payments'>): boolean =>
  compare(totalsOf(order).due, ZERO) === 0 && order.lines.length > 0

/** Money is in flight — a terminal is talking to a card, a QPay QR is on screen. */
export const hasPaymentInFlight = (order: Pick<OrderDraft, 'payments'>): boolean =>
  order.payments.some((p) => p.state === 'pending')

/**
 * Whether it is safe to swap the application bundle right now.
 *
 * A reload between a terminal approval and the order POST is money taken for an
 * order nobody has. The service worker and the shell's own updater both ask
 * this before activating; see `docs/pwa.md`.
 */
export function canInterrupt(state: {
  cartEmpty: boolean
  paymentInFlight: boolean
  modalOpen: boolean
  onWelcomeScreen: boolean
}): boolean {
  return state.cartEmpty && !state.paymentInFlight && !state.modalOpen && state.onWelcomeScreen
}

/**
 * Whether a reload *somebody asked for* can happen now.
 *
 * `canInterrupt` answers for the reloads nobody asked for — a service worker
 * that finished downloading, a shell update that landed at 11am. Those refuse a
 * basket, a modal and every screen past welcome, because waiting costs them
 * nothing: the swap is just as good in ten minutes.
 *
 * The shell's «Хуудсыг дахин ачаалах» is the other case, and the same rule was
 * wrong for it. A person is standing at the machine, and the reason anybody
 * reaches for that item is a render that has stopped answering — so the basket
 * on the screen in front of them is not a reason to refuse, and «wait for the
 * welcome screen» is a wait a wedged page never ends. Money is the only
 * refusal: while a terminal is talking to a card there is an approval that has
 * not reached the outbox, and that is the single thing a reload destroys.
 *
 * It is a deferral rather than a no. Once the charge has settled the request is
 * still owed, and the page holds it — see `shell/update.ts`.
 */
export const canReloadOnRequest = (state: { paymentInFlight: boolean }): boolean => !state.paymentInFlight

export type MismatchReason =
  | { kind: 'total'; claimed: Money; computed: Money }
  | { kind: 'line'; lineId: string; claimed: Money; computed: Money }
  | { kind: 'line-count'; claimed: number; computed: number }

/**
 * Does the page's arithmetic agree with the server's?
 *
 * The page prices the basket so the customer sees a total without a round trip;
 * the server re-prices it because the page is a thing anybody can open the
 * developer tools on. Both run this module, so a disagreement is a stale
 * catalogue on one side — which is a real condition and reads as one, rather
 * than as a customer being overcharged.
 */
export function reconcile(
  claimed: { total: Money; lines: readonly { id: string; total: Money }[] },
  computed: readonly PricedLine[]
): MismatchReason | null {
  if (claimed.lines.length !== computed.length) {
    return { kind: 'line-count', claimed: claimed.lines.length, computed: computed.length }
  }

  const byId = new Map(computed.map((l) => [l.selection.id, l]))
  for (const line of claimed.lines) {
    const match = byId.get(line.id)
    if (match === undefined) return { kind: 'line', lineId: line.id, claimed: line.total, computed: ZERO }
    if (compare(line.total, match.total) !== 0) {
      return { kind: 'line', lineId: line.id, claimed: line.total, computed: match.total }
    }
  }

  const computedTotal = computed.length === 0 ? ZERO : sum(computed.map((l) => l.total))
  if (compare(claimed.total, computedTotal) !== 0) {
    return { kind: 'total', claimed: claimed.total, computed: computedTotal }
  }

  return null
}

/**
 * The line items 1C's `CreateTalon` wants.
 *
 * Two things differ from `create_check`, which this replaced, and neither
 * endpoint accepts the other's spelling: an add-on's count is `quantity`, not
 * `quantity_adds`, and the two totals **include the add-ons** — 1C's own sample
 * is `selling_price: 7000` beside a ₮1,300 add-on and `total_amount: 8300`.
 * `selling_price` and `discounted_price` stay the drink alone.
 *
 * Ported from `checkout-api.ts:buildCheckoutPayload`, minus the reconstruction:
 * that function derived a base price by subtracting the change option and every
 * add-on back out of the flattened cart price, because the cart no longer held
 * them separately. Here they were never flattened.
 */
export interface CheckoutLine {
  readonly sku: Sku
  readonly quantity: number
  readonly selling_price: number
  readonly total_amount: number
  readonly discount_rate: number
  readonly discounted_price: number
  readonly discounted_total_amount: number
  /**
   * `quantity` is the add-on's count across the whole line — two drinks with
   * one shot each is `2`. Whether 1C means per line or per unit is still an
   * open question to 1C; every verified call used quantity 1, where the two
   * agree. See `docs/createtalon.md`.
   */
  readonly adds: readonly { sku: Sku; quantity: number; price: number }[]
}

export function toCheckoutLines(lines: readonly PricedLine[]): CheckoutLine[] {
  return lines.map((line) =>
    checkoutLine({
      sku: line.effectiveSku,
      quantity: line.selection.quantity,
      selling: line.sellingPrice,
      discounted: line.discountedSellingPrice,
      adds: line.addOns.map((a) => ({ sku: a.addOn.code, quantity: a.quantity, price: a.addOn.price }))
    })
  )
}

/**
 * One 1C line from amounts still in `Money`.
 *
 * The one place the shape is built, for `toCheckoutLines` here and the server's
 * `checkoutLinesFor`, which rebuilds it from stored rows. Every total is summed
 * as `Money` and converted last with `toTugrik`: 1C takes a fraction, and a
 * sum of converted numbers is a float that has been through a multiplication.
 * This floored with `Number(m / 100n)` — a ₮9.90 promotion went out as ₮9.
 */
export function checkoutLine(line: {
  sku: Sku
  quantity: number
  selling: Money
  discounted: Money
  /** `quantity` per unit of the drink; multiplied by the line's quantity here. */
  adds: readonly { sku: Sku; quantity: number; price: Money }[]
}): CheckoutLine {
  const adds = line.adds.map((a) => ({ sku: a.sku, quantity: a.quantity * line.quantity, price: a.price }))
  const addsTotal = sum(adds.map((a) => times(a.price, a.quantity)))
  return {
    sku: line.sku,
    quantity: line.quantity,
    selling_price: toTugrik(line.selling),
    total_amount: toTugrik(add(times(line.selling, line.quantity), addsTotal)),
    discount_rate:
      line.selling === ZERO ? 0 : Math.round((Number(line.selling - line.discounted) / Number(line.selling)) * 100),
    discounted_price: toTugrik(line.discounted),
    discounted_total_amount: toTugrik(add(times(line.discounted, line.quantity), addsTotal)),
    adds: adds.map((a) => ({ sku: a.sku, quantity: a.quantity, price: toTugrik(a.price) }))
  }
}

/**
 * What 1C's `typeofpayment` expects for each method.
 *
 * **From a kiosk, 1C takes `card` or `qpay`, and nothing else** (confirmed
 * 2026-09-17). **From a staffed POS till — `ispos: "1"` — it takes `cash`
 * as well**, which is the one place cash is a real tender (1C, 2026-09-28).
 * `cash` is not a value it accepts: its accumulation register refuses the
 * filing with `Record is invalid! "Эквайринг терминал" field value cannot be
 * empty! (Тооцоо эквайрингаар)`, because every sale it books is an acquiring
 * one.
 *
 * So cash files as `card`, and the honest consequence is that 1C's card
 * takings include money that never crossed a terminal. That is tolerable for
 * exactly one reason: **cash is a testing tender.** It is shown on a kiosk
 * while a shop is being set up and switched off for production by clearing
 * `payment_method.is_active`, which `routers/branch.ts` already filters on. A
 * production shop that offers cash has been misconfigured, and the reconciling
 * an accountant would have to do is the symptom.
 *
 * This exists so the mapping has **one** home. It was written and then called
 * by nothing: `services/checkout.ts` sent `order_payment.method` raw, so two of
 * the three values were right by coincidence of spelling and `cash` went out as
 * a value 1C has never accepted.
 */
export function checkoutPaymentType(method: PaymentMethod, pos = false): string {
  switch (method) {
    case 'cash':
      return pos ? 'cash' : 'card'
    case 'card':
      return 'card'
    case 'qpay':
      return 'qpay'
  }
}

/** The order number a customer waits for. Short, per branch, per day. */
export function displayNumber(sequence: number): string {
  return String(sequence % 1000).padStart(3, '0')
}

export function applyPayment(order: OrderDraft, payment: Payment): OrderDraft {
  const existing = order.payments.findIndex((p) => p.id === payment.id)
  const payments =
    existing === -1 ? [...order.payments, payment] : order.payments.map((p, i) => (i === existing ? payment : p))
  return { ...order, payments }
}

/** What is still owed, for the next payment method the customer picks. */
export function remainingDue(order: Pick<OrderDraft, 'lines' | 'payments'>): Money {
  return totalsOf(order).due
}
